import SectionHeading from '@/components/base/SectionHeading';
import Reveal from '@/components/feature/Reveal';

interface Session {
  id: string;
  name: string;
  time: string;
  status: 'Standby' | 'Active' | 'Peak';
  active: boolean;
  text: string;
}

const SESSIONS: Session[] = [
  {
    id: 'asian',
    name: 'Asian Session',
    time: '00:00 – 07:00',
    status: 'Standby',
    active: false,
    text: 'Thin liquidity. The EA watches structure but rarely opens new positions.',
  },
  {
    id: 'london',
    name: 'London Session',
    time: '07:00 – 12:00',
    status: 'Active',
    active: true,
    text: 'Structure breaks and trend continuation as European volume arrives. Entries are taken on the pullback after the break.',
  },
  {
    id: 'overlap',
    name: 'London / NY Overlap',
    time: '12:00 – 16:00',
    status: 'Peak',
    active: true,
    text: 'The highest-volume window — where most GoldWarden entries are opened.',
  },
  {
    id: 'ny',
    name: 'New York Session',
    time: '16:00 – 21:00',
    status: 'Active',
    active: true,
    text: 'Trend and news-driven moves; open trades are trailed and protected.',
  },
  {
    id: 'rollover',
    name: 'Rollover / Late',
    time: '21:00 – 00:00',
    status: 'Standby',
    active: false,
    text: 'No new entries. Anything still open as liquidity fades is closed before rollover, when gold spreads quietly widen.',
  },
];

interface SessionStyle {
  isPeak: boolean;
  badge: string;
  node: string;
  label: string;
}

/* Resolve the colour treatment once so the horizontal rail and the vertical
   fallback stay visually identical. */
function styleFor(session: Session): SessionStyle {
  const isPeak = session.status === 'Peak';
  return {
    isPeak,
    badge: isPeak
      ? 'bg-accent-100 text-accent-800'
      : session.active
        ? 'bg-primary-100 text-primary-700'
        : 'bg-secondary-100 text-secondary-700',
    node: isPeak
      ? 'bg-accent-500 ring-accent-200 animate-pulse-dot'
      : session.active
        ? 'bg-primary-500 ring-primary-200'
        : 'bg-secondary-400 ring-secondary-200',
    label: isPeak ? 'text-accent-700' : 'text-foreground-500',
  };
}

function StatusPill({ session, style }: { session: Session; style: SessionStyle }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide ${style.badge}`}
    >
      <span
        className={`w-1.5 h-1.5 rounded-full ${session.active ? (style.isPeak ? 'bg-accent-500' : 'bg-primary-500') : 'bg-secondary-400'}`}
        aria-hidden="true"
      ></span>
      {session.status}
    </span>
  );
}

export default function SessionTimeline() {
  return (
    <section id="sessions" className="py-14 md:py-20 bg-background-50 scroll-mt-32 md:scroll-mt-36">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-shell mx-auto">
          <Reveal>
            <SectionHeading
              eyebrow="Sessions"
              title="It only works when gold actually moves"
              subtitle="GoldWarden is session-aware. It concentrates activity in the liquid London and New York windows and stands down in the quiet hours."
            />
          </Reveal>

          {/* Horizontal "day flow" rail — desktop */}
          <div className="hidden lg:grid grid-cols-5 mt-12">
            {SESSIONS.map((session, index) => {
              const style = styleFor(session);
              return (
                <Reveal key={session.id} delay={index * 70}>
                  <div className="relative px-4 h-full">
                    <StatusPill session={session} style={style} />

                    <div className="relative mt-6 h-4" aria-hidden="true">
                      <span className="absolute top-1/2 left-0 right-0 h-px bg-background-300"></span>
                      <span
                        className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full ring-4 ${style.node}`}
                      ></span>
                    </div>

                    <p className={`mt-6 font-num text-xs font-semibold ${style.label}`}>{session.time}</p>
                    <h3 className="mt-1 font-heading text-sm font-semibold text-foreground-950">
                      {session.name}
                    </h3>
                    <p className="mt-2 text-xs leading-relaxed text-foreground-600">{session.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          {/* Vertical fallback — tablet & mobile */}
          <div className="lg:hidden relative mt-10 md:mt-12">
            <span
              className="absolute left-[7px] top-2 bottom-2 w-px bg-background-300"
              aria-hidden="true"
            ></span>
            <div className="flex flex-col gap-8">
              {SESSIONS.map((session, index) => {
                const style = styleFor(session);
                return (
                  <Reveal key={session.id} delay={index * 60}>
                    <div className="relative pl-8">
                      <span
                        className={`absolute left-0 top-1.5 w-3.5 h-3.5 rounded-full ring-4 ${style.node}`}
                        aria-hidden="true"
                      ></span>
                      <StatusPill session={session} style={style} />
                      <p className={`mt-3 font-num text-xs font-semibold ${style.label}`}>{session.time}</p>
                      <h3 className="mt-1 font-heading text-sm font-semibold text-foreground-950">
                        {session.name}
                      </h3>
                      <p className="mt-2 text-xs leading-relaxed text-foreground-600">{session.text}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <p className="mt-8 text-xs text-foreground-500">
            Times shown in server time (GMT+0). The EA adapts to your broker&rsquo;s server clock
            automatically.
          </p>
        </div>
      </div>
    </section>
  );
}